import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from, of } from 'rxjs';
import {
  concatMap,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import {
  requestNotificationPermission,
  setNotificationPermissionResult,
  showCallNotification,
} from '../actions';
import { selectIncomingCall } from '../selectors';

@Injectable()
export class ConnectNotificationEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  public requestNotificationPermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestNotificationPermission),
      switchMap(() =>
        from(Notification.requestPermission()).pipe(
          map((result) => setNotificationPermissionResult({ result })),
        ),
      ),
    ),
  );

  public showCallNotification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(showCallNotification),
        concatMap((action) =>
          of(action).pipe(
            withLatestFrom(this.store.select(selectIncomingCall)),
          ),
        ),
        filter(
          ([, incomingCall]) =>
            isNonNullish(incomingCall) && this.document.hidden,
        ),
        tap(([, incomingCall]) => {
          const options: NotificationOptions = {
            body: $localize`:Connect|Incoming call notification@@incomingCallNotificationText:${incomingCall?.contact.displayName} is calling you`,
          };
          if (incomingCall?.contact.imageLink) {
            options.icon = incomingCall?.contact.imageLink;
          }
          const notification = new Notification('Pulse Connect', options);
          this.onNotificationClick(notification);
          this.onNotificationClose(notification);
        }),
      ),
    {
      dispatch: false,
    },
  );

  private onNotificationClick(notification: Notification) {
    notification.addEventListener('click', () => {
      window.focus();
      notification.close();
      notification.removeEventListener('click', () => null);
    });
  }

  private onNotificationClose(notification: Notification) {
    notification.addEventListener('close', () => {
      notification.close();
      notification.removeEventListener('close', () => null);
    });
  }
}
